import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Button, EmptyState, InlineError, Panel, Skeleton, Textarea } from '../../components/ui';
import { PageHeader } from '../../layouts/Shells';
import { isSuperAdmin, useMyPermissions } from '../auth/useMyPermissions';
import { localIsoDate } from '../myTasks/types';
import { CopyLinkButton } from '../sharing/CopyLinkButton';
import { briefShareUrl } from '../sharing/shareLinks';
import { BriefBody } from './BriefBody';
import { BriefLinksPanel } from './BriefLinksPanel';
import { BriefDealsSection } from './BriefDealsSection';
import { briefDateLabel, briefShortDate } from './briefMarkdown';
import { useBriefDeals, useBriefs, useSaveBrief } from './hooks';
import { briefErrorMessage, type CeoBrief } from './queries';

/**
 * The daily CEO operator brief, in the Hub.
 *
 * Kyu, 11 September: "I prefer centralized everything." It lived in a Google
 * Apps Script app on a Sheet, which shared nothing with the Hub: no auth, no
 * permissions, no design system, no archive anybody else could reach, and a
 * second place to change every time Alex changed his mind.
 *
 * The WRITING has not moved and is not meant to. An AI session produces the
 * brief and Kyu pastes it, which he asked for ("I actually dont mind copy
 * pasting the produced ceo brief, it's a few seconds thing"). This page is
 * the destination.
 *
 * ── Read first, write second ───────────────────────────────────────────────
 *
 * Alex reads this on his phone and never writes here. So the page opens on
 * today's brief, full width, nothing above it but the date. The paste box is
 * behind a button, because the person who needs it knows it exists and the
 * person who does not should never meet it.
 *
 * ── The date is the address ────────────────────────────────────────────────
 *
 * /ceo-brief opens the latest; /ceo-brief/2026-09-11 opens that day. Kyu,
 * 11 September: "make sure the CEO Brief tab will have a shareable link. and
 * archive." The archive chips are links to those addresses, so a brief can
 * be pasted into Slack and opened in one tap. The link grants nothing: RLS
 * on ceo_briefs still answers, and a non-super-admin sees the gate below.
 */

const TITLE = 'CEO Brief';
const BASE = '/ceo-brief';

export function CeoBriefPage() {
  const permissions = useMyPermissions();
  const briefs = useBriefs();
  const briefDeals = useBriefDeals();
  const save = useSaveBrief();
  const navigate = useNavigate();
  const { briefDate: showing } = useParams();

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [draftDate, setDraftDate] = useState(() => localIsoDate(new Date()));
  const [error, setError] = useState('');

  if (permissions.isPending) {
    return (
      <>
        <PageHeader title={TITLE} topbar />
        <Panel>
          <Skeleton lines={3} label="Checking access" />
        </Panel>
      </>
    );
  }

  // The database refuses regardless; this decides what to draw instead of a
  // page of failed queries.
  if (!isSuperAdmin(permissions.data)) {
    return (
      <>
        <PageHeader title={TITLE} topbar />
        <Panel>
          <EmptyState
            title="Super admins only"
            description="The CEO brief is limited to super admins."
          />
        </Panel>
      </>
    );
  }

  if (briefs.isPending) {
    return (
      <>
        <PageHeader title={TITLE} topbar />
        <Skeleton lines={8} label="Loading the brief" />
      </>
    );
  }

  const all = briefs.data ?? [];
  const current =
    showing === undefined ? all[0] : all.find((brief) => brief.brief_date === showing);

  function startEditing(body: string, date: string) {
    setDraft(body);
    setDraftDate(date);
    setError('');
    setEditing(true);
  }

  function submit() {
    if (draft.trim() === '') {
      setError('Paste the brief before saving.');
      return;
    }
    save.mutate(
      { briefDate: draftDate, body: draft },
      {
        onSuccess: () => {
          setEditing(false);
          setError('');
          void navigate(`${BASE}/${draftDate}`);
        },
        onError: (cause: unknown) =>
          setError(briefErrorMessage(cause instanceof Error ? cause.message : '')),
      },
    );
  }

  return (
    <>
      <PageHeader title={TITLE} topbar />

      <div className="brief-page">
        {error === '' ? null : <InlineError>{error}</InlineError>}

        {editing ? (
          <section className="brief-editor" aria-label="Paste the brief">
            <div className="brief-editor-head">
              <label className="brief-date-field">
                <span>Brief for</span>
                <input
                  type="date"
                  value={draftDate}
                  onChange={(event) => setDraftDate(event.target.value)}
                />
              </label>
              <div className="brief-editor-actions">
                <Button variant="secondary" size="sm" onClick={() => setEditing(false)}>
                  Cancel
                </Button>
                <Button size="sm" disabled={save.isPending} onClick={submit}>
                  {save.isPending ? 'Saving…' : 'Save brief'}
                </Button>
              </div>
            </div>
            <Textarea
              label="Brief"
              labelHidden
              rows={22}
              value={draft}
              placeholder="Paste the brief here. Markdown works: # headings, **bold**, - bullets."
              onChange={(event) => setDraft(event.target.value)}
            />
            {draft.trim() === '' ? null : (
              <div className="brief-preview">
                <p className="brief-preview-label">Preview</p>
                <BriefBody markdown={draft} />
                {/* The preview shows what Alex will see, and Alex sees the
                    starred deals under every brief. */}
                {briefDeals.data === null || briefDeals.data === undefined ? null : (
                  <BriefDealsSection deals={briefDeals.data} variant="hub" />
                )}
              </div>
            )}
          </section>
        ) : current === undefined && all.length === 0 ? (
          <EmptyState
            title="No brief yet"
            description="Paste the first one and it appears here, and in the archive from tomorrow."
            action={
              <Button size="sm" onClick={() => startEditing('', localIsoDate(new Date()))}>
                Paste a brief
              </Button>
            }
          />
        ) : current === undefined ? (
          <>
            {/* A link to a day that has no brief: an old link, a typo, or a
                brief that was deleted. Say so and offer the latest, rather
                than silently showing a different day under the asked-for URL. */}
            <EmptyState
              title={`No brief for ${briefDateLabel(showing ?? '')}`}
              description="There is nothing saved for that day. The latest brief is one tap away."
              action={
                <Button size="sm" onClick={() => void navigate(BASE)}>
                  Open the latest brief
                </Button>
              }
            />
            <BriefArchive briefs={all} currentDate={null} />
          </>
        ) : (
          <>
            <div className="brief-masthead">
              <div className="brief-masthead-row">
                <p className="brief-eyebrow">4ocean · TAP Division</p>
                {/* "Hub link", not "Copy link". This address is behind the
                    Hub's sign-in: a teammate can open it, and anybody on a
                    supplier account who opens it is bounced to the supplier
                    portal, which is what happened to Kyu on 14 September when
                    he shared it as though it were public. The public link is
                    a different address, /brief/<token>, minted separately.
                    Until the links panel exists, the honest fix is a label
                    that says which of the two this is. */}
                <CopyLinkButton
                  url={briefShareUrl(window.location.origin, current.brief_date)}
                  label="Copy Hub link"
                  className="brief-share-link"
                />
              </div>
              <h1 className="brief-date">{briefDateLabel(current.brief_date)}</h1>
            </div>

            <BriefBody markdown={current.body} />

            {/* Alex asked for the deals "at the bottom": after the day's
                story, before the tools. Live from the board, not pasted. */}
            {briefDeals.data === null || briefDeals.data === undefined ? null : (
              <BriefDealsSection deals={briefDeals.data} variant="hub" />
            )}

            {/* Below the brief, never above it: Alex opens this to read, and
                the controls are for the one person who writes. */}
            <div className="brief-tools">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => startEditing(current.body, current.brief_date)}
              >
                Edit this brief
              </Button>
              <Button size="sm" onClick={() => startEditing('', localIsoDate(new Date()))}>
                Paste today’s brief
              </Button>
            </div>

            {/* Below the tools, for the same reason the tools are below the
                brief: Alex opens this page to read it, and link management is
                for the one person who writes. Draws nothing for anybody who
                is not a super admin. */}
            <BriefLinksPanel />

            <BriefArchive briefs={all} currentDate={current.brief_date} />
          </>
        )}
      </div>
    </>
  );
}

/**
 * Every saved brief, newest first, as links. Each chip is a real address
 * (/ceo-brief/2026-09-11), so the back button, a bookmark and a pasted link
 * all land on the same day. Hidden while there is only one brief, because
 * a list of one thing that is already on screen is noise.
 */
function BriefArchive({
  briefs,
  currentDate,
}: {
  briefs: readonly CeoBrief[];
  currentDate: string | null;
}) {
  const navigate = useNavigate();
  if (briefs.length < 2 && (briefs.length === 0 || currentDate !== null)) return null;
  const today = new Date();
  return (
    <nav className="brief-archive" aria-label="Earlier briefs">
      <p className="brief-archive-label">Archive</p>
      <div className="brief-archive-list">
        {briefs.map((brief) => (
          <button
            key={brief.id}
            type="button"
            className={`brief-archive-chip${brief.brief_date === currentDate ? ' is-on' : ''}`}
            aria-pressed={brief.brief_date === currentDate}
            onClick={() => void navigate(`${BASE}/${brief.brief_date}`)}
          >
            {briefShortDate(brief.brief_date, today)}
          </button>
        ))}
      </div>
    </nav>
  );
}
